import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.pexels.com/photos/6875526/pexels-photo-6875526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const LINES = [
  { text: "World-class healthcare.", accent: false },
  { text: "Uncompromising luxury.", accent: false },
  { text: "70–90% lower costs.", accent: true },
];

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden bg-brand-deep text-ivory" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-48 -right-48 h-[38rem] w-[38rem] rounded-full bg-brand-glow/50 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-[26rem] w-[26rem] rounded-full bg-gold/10 blur-[120px]" />

      <div className="relative mx-auto grid min-h-screen max-w-7xl items-center gap-14 px-6 pb-24 pt-40 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold"
            data-testid="hero-overline"
          >
            Medical travel, reimagined — USA · UK · Europe → India
          </motion.p>

          <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic text-gold" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.3 + i * 0.15, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ivory/70 sm:text-lg"
          >
            OravioCare is your private medical concierge — guiding you from your first video consult to
            surgery at India's finest JCI-accredited hospitals, and recovery in five-star comfort.
            One transparent package. No waiting lists. No surprises.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-quote-btn"
              onClick={(e) => scrollTo(e, "#quote")}
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-brand-deep transition-colors duration-300 hover:bg-ivory"
            >
              Get a free quote
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-ivory/10 pt-8"
          >
            {[
              ["85%", "average savings vs US prices"],
              ["JCI", "accredited partner hospitals"],
              ["24/7", "dedicated care manager"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-serif text-3xl text-gold">{big}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50">{small}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
            className="relative overflow-hidden rounded-t-[11rem] rounded-b-3xl"
          >
            <motion.img
              src={HERO_IMG}
              alt="Patient recovering in luxury care"
              style={{ y: imgY }}
              className="h-[26rem] w-full scale-110 object-cover sm:h-[32rem]"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/50 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            className="absolute -left-4 bottom-10 sm:-left-10"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-2xl border border-sand bg-ivory px-5 py-4 shadow-2xl"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Trusted by patients from</p>
              <p className="mt-1 font-serif text-lg font-semibold text-ink">30+ US states & 12 EU countries</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/40">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px origin-top bg-gold"
        />
      </motion.div>
    </section>
  );
}
